import React from 'react'
import { FiUser } from 'react-icons/fi'
import { useAvatarImage } from '@/hooks/useAvatarImage'
import LoadingSpinner from './LoadingSpinner'

interface AvatarProps {
    avatarId?: string | null
    alt?: string
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
    className?: string
    showBorder?: boolean
    showLoadingSpinner?: boolean
    fallbackIcon?: React.ReactNode
}

const Avatar: React.FC<AvatarProps> = ({
    avatarId,
    alt = 'Avatar',
    size = 'md',
    className = '',
    showBorder = true,
    showLoadingSpinner = true,
    fallbackIcon
}) => {
    const { src, loading, error } = useAvatarImage({ avatarId })

    const sizeClasses = {
        xs: 'w-6 h-6',
        sm: 'w-8 h-8',
        md: 'w-10 h-10',
        lg: 'w-12 h-12',
        xl: 'w-16 h-16',
        '2xl': 'w-20 h-20'
    }

    const iconSizes = {
        xs: 'w-3 h-3',
        sm: 'w-4 h-4',
        md: 'w-5 h-5',
        lg: 'w-6 h-6',
        xl: 'w-8 h-8',
        '2xl': 'w-10 h-10'
    }

    const spinnerSizes = {
        xs: 'xs' as const,
        sm: 'sm' as const,
        md: 'sm' as const,
        lg: 'md' as const,
        xl: 'md' as const,
        '2xl': 'lg' as const
    }

    const borderClass = showBorder ? 'border-2 border-gray-200 dark:border-gray-600' : ''
    const baseClasses = `${sizeClasses[size]} rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center ${borderClass} ${className}`

    // Show loading state
    if (loading && showLoadingSpinner) {
        return (
            <div className={baseClasses}>
                <LoadingSpinner size={spinnerSizes[size]} />
            </div>
        )
    }

    // Show image if available
    if (src && !error) {
        return (
            <div className={baseClasses}>
                <img
                    src={src}
                    alt={alt}
                    className="w-full h-full object-cover"
                    onError={() => {
                        console.warn('Avatar image failed to load:', avatarId)
                    }}
                />
            </div>
        )
    }

    // Show fallback
    return (
        <div className={baseClasses}>
            {fallbackIcon || (
                <FiUser className={`${iconSizes[size]} text-gray-400 dark:text-gray-500`} />
            )}
        </div>
    )
}

export default Avatar